import { isMaxLength, isMinLength } from "@formiz/validations";
import { SubscriptionTiers, useTranslation } from "@next-platform/client-common-core";
import {
  CapabilityGate,
  SubCapabilityKey,
  useApplicationCapabilitiesAllowed,
} from "@next-platform/client-common-core/infra/application/capabilities";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import type { RulebookAttachmentSelection } from "@next-platform/client-common-core/rulebook/attach/useRulebookAttachmentSelection";
import {
  Badge,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Icon,
  IconSize,
  IconVariants,
  Size,
  Switch,
  Text,
  TextArea,
  TextVariants,
} from "@next-platform/client-common-spicy-ui";
import { Lorebook } from "@next-platform/packages-ts-types";
import { Accordion, AccordionItem } from "@nextui-org/react";
import React, { useCallback, useEffect, useState } from "react";

import ConfirmationModal from "@/components/common/ConfirmationModal";
import LorebookSection from "@/components/common/LorebookSection";
import { FormField } from "@/components/customComponents";
import RulebookAttachmentField from "@/components/Rulebook/RulebookAttachmentField";
import Trans from "@/components/Trans/Trans";
import { ADVANCED_SECTION_KEY, CHARACTER_FORM_VALIDATION_SCHEMA, EXTERNAL_ROUTES_FOR_APPLICATION, FEATURE_FLAGS } from "@/constants";
import { useFeatureFlag } from "@/hooks";
import { cn, getTierBadgeColors } from "@/utils";

import { PLACEHOLDER_STYLING } from "../constants";
import { CharacterFormFields, SuggestedFieldButtonProps } from "../types";
import ModalLorebook from "./ModalLorebook";
import getCharacterFormPlaceholders from "./utils/getCharacterFormPlaceholders";

interface ICharacterFormAdvancedProps {
  getCharAndTokenLabel: (field: CharacterFormFields) => string;
  waitingForApproval: boolean;
  suggestedFieldButton: React.ComponentType<SuggestedFieldButtonProps>;
  characterId?: string;
  lorebooks?: Lorebook[];
  /** Rulebook picked for the character (applied on save); absent hides the section. */
  rulebookSelection?: RulebookAttachmentSelection;
  voiceId?: string;
  voiceName?: string;
  voiceDescription?: string;
  shouldExpandAdvanced?: boolean;
  onLorebookAttached?: (lorebook: Lorebook) => void;
  onLorebookDetached?: (lorebookId: string) => void;
  onCreateVoiceClick?: () => void;
  onAttachVoiceClick?: () => void;
  onRemoveVoiceClick?: () => void;
  groupAddable: boolean;
  onGroupAddableChange: (value: boolean) => void;
  isBotVisibilityPublic: boolean;
}

const CharacterFormAdvanced = ({
  getCharAndTokenLabel,
  waitingForApproval,
  suggestedFieldButton: SuggestedFieldButton,
  characterId,
  lorebooks,
  rulebookSelection,
  voiceId,
  voiceName,
  voiceDescription,
  shouldExpandAdvanced,
  onLorebookAttached,
  onLorebookDetached,
  onCreateVoiceClick,
  onAttachVoiceClick,
  onRemoveVoiceClick,
  groupAddable,
  onGroupAddableChange,
  isBotVisibilityPublic,
}: ICharacterFormAdvancedProps) => {
  const [stateExpandedKeys, setStateExpandedKeys] = useState<string[]>([]);
  const [stateIsModalLorebookOpen, setStateIsModalLorebookOpen] = useState(false);
  const [stateIsDetachConfirmationOpen, setStateIsDetachConfirmationOpen] = useState(false);
  const applicationId = basicAppConfig.applicationId;
  const { hasFeatureFlag: hasCustomVoiceFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.TTS);
  const { hasFeatureFlag: hasVoiceAttachmentFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.CHARACTER_VOICE_ATTACHMENT);
  const shouldShowVoiceSection = hasCustomVoiceFeatureFlag || hasVoiceAttachmentFeatureFlag;
  const isTtsManageCustomVoiceAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.TtsManageCustomVoice);
  const isGroupChatManageAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.GroupChatManage);
  const lorebook = lorebooks?.[0];
  const { t } = useTranslation();
  const formPlaceholders = getCharacterFormPlaceholders(t);
  // Auto-expand Advanced section when validation errors occur
  useEffect(() => {
    if (shouldExpandAdvanced) {
      setStateExpandedKeys([ADVANCED_SECTION_KEY]);
    }
  }, [shouldExpandAdvanced]);

  const handleDetachLorebook = () => {
    setStateIsDetachConfirmationOpen(true);
  };

  const confirmDetachLorebook = async () => {
    if (!lorebook?.id) return;

    onLorebookDetached?.(lorebook.id);
    setStateIsDetachConfirmationOpen(false);
  };

  const isAdvancedSectionOpen = stateExpandedKeys.includes(ADVANCED_SECTION_KEY);

  const handleToggleAdvancedSection = useCallback(() => {
    setStateExpandedKeys((previousKeys) => (previousKeys.includes(ADVANCED_SECTION_KEY) ? [] : [ADVANCED_SECTION_KEY]));
  }, []);

  return (
    <>
      <Accordion keepContentMounted hideIndicator className="px-0" selectedKeys={stateExpandedKeys}>
        <AccordionItem
          key={ADVANCED_SECTION_KEY}
          as="div"
          hideIndicator
          classNames={{
            trigger: "bg-transparent cursor-pointer font-semibold h-5",
            title: "text-[24px] tracking-tighter",
            indicator: "text-[20px]",
          }}
          HeadingComponent={() => (
            <Button
              variant={ButtonVariant.OUTLINED}
              size={ButtonSize.MEDIUM}
              type="button"
              onClick={handleToggleAdvancedSection}
              iconEnd={isAdvancedSectionOpen ? IconVariants.CHEVRON_UP : IconVariants.CHEVRON_DOWN}
              className="!outline-none"
            >
              <Trans i18nKey="chatbot:form.advancedButton" />
            </Button>
          )}
        >
          <div className="flex flex-col gap-md">
            <CapabilityGate capability={SubCapabilityKey.LorebookCharacterAttach}>
              <LorebookSection
                attachedLorebook={lorebook}
                cardVariant="static"
                dataTestId="CharacterFormAdvancedLorebookSectionCapabilityGate"
                onAttach={() => setStateIsModalLorebookOpen(true)}
                onReplace={() => setStateIsModalLorebookOpen(true)}
                onDetach={handleDetachLorebook}
                disabled={waitingForApproval}
              />
            </CapabilityGate>
            {rulebookSelection ? (
              <RulebookAttachmentField
                selection={rulebookSelection}
                disabled={waitingForApproval}
                dataTestId="CharacterFormAdvanced-RulebookSection"
              />
            ) : null}
            <CapabilityGate capability={SubCapabilityKey.TtsCharacterAttach}>
              {shouldShowVoiceSection ? (
                <div data-testid="CharacterFormAdvanced-VoiceSection" className="flex flex-col gap-2">
                  <div className="flex flex-row gap-2 items-center">
                    <Text variant={TextVariants.LABEL_LARGE_MEDIUM} className="vertical-align-middle">
                      <Trans i18nKey="voice:assignment.title" />
                    </Text>
                    <Badge showDot={false} variant={BadgeVariant.OUTLINED} className={cn(getTierBadgeColors(SubscriptionTiers.PREMIUM))}>
                      {SubscriptionTiers.PREMIUM}
                    </Badge>
                  </div>
                  {hasVoiceAttachmentFeatureFlag && voiceId ? (
                    <div className="flex items-start gap-3 rounded-2xl border border-solid border-gray-5 bg-gray-2 p-3">
                      <div className="flex size-[42px] shrink-0 items-center justify-center rounded-full bg-black dark:bg-white">
                        <Icon icon={IconVariants.PLAY} size={IconSize.MEDIUM} className="fill-white dark:fill-black dark:stroke-black" />
                      </div>
                      <div className="min-w-0">
                        <Text variant={TextVariants.LABEL_LARGE_MEDIUM}>{voiceName || t("voice:assignment.attachedVoice.title")}</Text>
                        <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
                          {voiceDescription || t("voice:assignment.attachedVoice.description")}
                        </Text>
                      </div>
                    </div>
                  ) : null}
                  {hasVoiceAttachmentFeatureFlag && voiceId ? (
                    <div className="flex flex-row flex-wrap items-center gap-2">
                      <Button
                        variant={ButtonVariant.OUTLINED}
                        size={ButtonSize.MEDIUM}
                        iconStart={IconVariants.REFRESH}
                        onClick={onAttachVoiceClick}
                        disabled={waitingForApproval || !onAttachVoiceClick}
                        type="button"
                      >
                        <Trans i18nKey="voice:assignment.cta.change" />
                      </Button>
                      <Button
                        variant={ButtonVariant.OUTLINED}
                        size={ButtonSize.MEDIUM}
                        iconStart={IconVariants.TRASH}
                        onClick={onRemoveVoiceClick}
                        disabled={waitingForApproval || !onRemoveVoiceClick}
                        type="button"
                      />
                    </div>
                  ) : (
                    <div className="flex flex-row flex-wrap gap-2">
                      {hasCustomVoiceFeatureFlag && isTtsManageCustomVoiceAllowed ? (
                        <Button
                          data-testid="CharacterFormAdvanced-CreateVoiceButton"
                          variant={ButtonVariant.OUTLINED}
                          size={ButtonSize.MEDIUM}
                          iconStart={IconVariants.PLUS}
                          onClick={onCreateVoiceClick}
                          disabled={waitingForApproval || !onCreateVoiceClick}
                          type="button"
                        >
                          <Trans i18nKey="voice:assignment.cta.create" />
                        </Button>
                      ) : null}
                      {hasVoiceAttachmentFeatureFlag ? (
                        <Button
                          data-testid="CharacterFormAdvanced-AttachVoiceButton"
                          variant={ButtonVariant.OUTLINED}
                          size={ButtonSize.MEDIUM}
                          iconStart={IconVariants.UPLOAD}
                          onClick={onAttachVoiceClick}
                          disabled={waitingForApproval || !onAttachVoiceClick}
                          type="button"
                        >
                          <Trans i18nKey="voice:assignment.cta.attach" />
                        </Button>
                      ) : null}
                    </div>
                  )}
                  {!voiceId ? (
                    <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
                      {hasVoiceAttachmentFeatureFlag ? (
                        <Trans i18nKey="voice:assignment.helper" />
                      ) : (
                        <Trans i18nKey="voice:assignment.helper.createOnly" />
                      )}
                    </Text>
                  ) : null}
                </div>
              ) : null}
            </CapabilityGate>
            <FormField
              as="textarea"
              name={CharacterFormFields.SCENARIO}
              data-field-name={CharacterFormFields.SCENARIO}
              validations={[
                {
                  rule: (value: string) => isMinLength(3)(value?.trim()),
                  message: t("chatbot:validation.field.minLength", { minLength: 3 }),
                },
                {
                  rule: (value: string) => isMaxLength(4000)(value?.trim()),
                  message: t("chatbot:validation.scenario.maxLength", { maxLength: 4000 }),
                },
              ]}
            >
              <TextArea
                label={t("chatbot:form.label.scenario")}
                labelRightElement={<SuggestedFieldButton field="scenario" />}
                countLabel={getCharAndTokenLabel(CharacterFormFields.SCENARIO)}
                showMaxLengthLabel={false}
                disabled={waitingForApproval}
                helperText={t("chatbot:form.description.scenario")}
                maxLength={CHARACTER_FORM_VALIDATION_SCHEMA[CharacterFormFields.SCENARIO].maxLength}
                rows={4}
                aria-labelledby={t("chatbot:form.label.scenario")}
                placeholder={formPlaceholders[CharacterFormFields.SCENARIO]}
                fieldInfoLink={
                  EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.create_chatbot_docs?.scenario
                }
                className={PLACEHOLDER_STYLING}
              />
            </FormField>
            <FormField
              as="textarea"
              name={CharacterFormFields.DIALOGUE}
              data-field-name={CharacterFormFields.DIALOGUE}
              validations={[
                {
                  rule: (value: string) => isMinLength(3)(value?.trim()),
                  message: t("chatbot:validation.field.minLength", { minLength: 3 }),
                },
                {
                  rule: (value: string) => isMaxLength(4000)(value?.trim()),
                  message: t("chatbot:validation.dialogue.maxLength", { maxLength: 4000 }),
                },
              ]}
            >
              <TextArea
                label={t("chatbot:form.label.dialogue")}
                labelRightElement={<SuggestedFieldButton field="dialogue" />}
                helperText={t("chatbot:form.description.dialogue")}
                disabled={waitingForApproval}
                maxLength={CHARACTER_FORM_VALIDATION_SCHEMA[CharacterFormFields.DIALOGUE].maxLength}
                rows={2}
                maxRows={20}
                showMaxLengthLabel={false}
                aria-labelledby={t("chatbot:form.label.dialogue")}
                placeholder={formPlaceholders[CharacterFormFields.DIALOGUE]}
                countLabel={getCharAndTokenLabel(CharacterFormFields.DIALOGUE)}
                fieldInfoLink={
                  EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.create_chatbot_docs?.dialogue
                }
                className={PLACEHOLDER_STYLING}
              />
            </FormField>
            {isGroupChatManageAllowed ? (
              <>
                <div className="border-t border-solid border-gray-5" />
                <div className="flex flex-row items-center justify-between gap-4" data-testid="CharacterFormAdvanced-AllowInGroupsRow">
                  <div className={cn("flex flex-col", { "opacity-50": !isBotVisibilityPublic })}>
                    <Text variant={TextVariants.LABEL_LARGE_MEDIUM}>
                      <Trans i18nKey="chatbot:form.label.allowInGroups" />
                    </Text>
                    <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
                      <Trans i18nKey="chatbot:form.description.allowInGroups" />
                    </Text>
                  </div>
                  <FormField name={CharacterFormFields.GROUP_ADDABLE} as="custom">
                    <Switch
                      data-testid="CharacterFormAdvanced-AllowInGroupsSwitch"
                      checked={groupAddable}
                      onCheckedChange={(checked: boolean) => {
                        onGroupAddableChange(checked);
                      }}
                      disabled={waitingForApproval || !isBotVisibilityPublic}
                      size={Size.LARGE}
                    />
                  </FormField>
                </div>
              </>
            ) : null}
          </div>
        </AccordionItem>
      </Accordion>
      <CapabilityGate capability={SubCapabilityKey.LorebookCharacterAttach}>
        <ModalLorebook
          isOpen={stateIsModalLorebookOpen}
          onClose={() => setStateIsModalLorebookOpen(false)}
          selectedLorebook={lorebook}
          onLorebookAttached={onLorebookAttached}
          dataTestId="CharacterFormAdvancedModalLorebookCapabilityGate"
        />
      </CapabilityGate>
      <ConfirmationModal
        heading={t("createGroup:modal.detachLorebook.title")}
        handleClose={() => setStateIsDetachConfirmationOpen(false)}
        onUserConfirmation={confirmDetachLorebook}
        confirmationMessage={t("createGroup:modal.detachLorebook.message")}
        isVisible={stateIsDetachConfirmationOpen}
        isLoading={false}
        actionButtonColor="error"
        actionButtonText={t("createGroup:modal.detachLorebook.action")}
        discardButtonText={t("common:cancel")}
      />
    </>
  );
};

export default CharacterFormAdvanced;
